/**
 * The published suggestion index, as one line per word.
 *
 * The build writes this file and a search box in the browser reads it back, so
 * the two have to agree about the separator, the column order and how a
 * reading is spelled. Both ends import this module for that reason. See
 * `src/assets/cidian-suggestions.ts` for the writing and
 * `src/site/suggestion-index.ts` for the reading.
 * `./cidian-reading-spelling.ts` is the other half of the contract: this is
 * what a line holds and that is how a query is compared with one.
 *
 * **This is not the address index.** `./cidian-index-line.ts` publishes every
 * 词典 address and its band, for a page that has segmented a text and is
 * deciding which of the words it found to link. This publishes the words a
 * reader might be about to type, in the order a list of them should be offered
 * in, with the reading each is looked up by.
 *
 * **There is one file per writing and therefore no column for it.** 认识 and
 * 認識 are one word with a page each and a row each, and a reader is shown one
 * writing. Publishing both in one file and dropping half of it in the browser
 * costs a 简体 reader 38 KB brotli of rows they will never be offered, so the
 * build writes two files and the page fetches the one it is drawn in. See
 * `src/assets/cidian-suggestions.ts`.
 *
 * **The reading is held one syllable at a time**, in the encoding
 * `./cidian-syllables.ts` writes. A box can colour the reading it offers, and
 * a box can tell 情 from 请 for somebody who typed `qing2`. It costs 4 KB
 * brotli over the 简体 file.
 */

import {
  type CidianSyllable,
  decodeSyllables,
  encodeSyllables,
  refuseUnreadable,
  refuseUnwritable,
  syllablesText,
} from "./cidian-syllables.js";

/** What separates a word from its reading. */
export const columnSeparator = "\t";

/** One word a reader might mean, as the index holds it. */
export interface CidianSuggestion {
  /** The headword, as its address and the box both spell it. */
  readonly word: string;

  /** How it is read, one syllable at a time, in the order they are said. */
  readonly syllables: readonly CidianSyllable[];
}

/**
 * One word as a line, or a refusal naming what stopped it.
 *
 * The word and the reading as the section spells it are both refused what
 * would corrupt the file quietly. `cidian-index-line.ts` makes the same check
 * about the same words. See {@link refuseUnwritable}.
 */
export function cidianSuggestionLine(suggestion: CidianSuggestion): string {
  refuseUnwritable(suggestion.word, suggestion.word);
  refuseUnreadable(suggestion.word, syllablesText(suggestion.syllables));

  const reading = encodeSyllables(suggestion.syllables);

  return `${suggestion.word}${columnSeparator}${reading}`;
}

/**
 * The whole index, in the order the file holds it.
 *
 * That order is the ranking. The build writes the words a learner meets first
 * first, so a box offering the first few matches it walks past is offering the
 * best few, and nothing here has to score anything.
 *
 * A line this cannot read is dropped rather than refused. What the index is
 * for is offering a reader a shortcut, and a file that arrived truncated
 * should cost them some suggestions rather than the box they were typing in.
 */
export function readCidianSuggestions(
  text: string,
): readonly CidianSuggestion[] {
  const suggestions: CidianSuggestion[] = [];

  for (const line of text.split("\n")) {
    const [word, reading] = line.split(columnSeparator);

    if (word !== undefined && word !== "" && reading !== undefined) {
      suggestions.push({ word, syllables: decodeSyllables(reading) });
    }
  }

  return suggestions;
}
